import React from 'react';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, DollarSign } from 'lucide-react';
import { PieChart } from './PieChart';
import { InfoTooltip } from '@/components/InfoTooltip';

export const CostBreakdown = ({ cogs, loading, quantityPerPlate = 1, modelMetrics, materialName, printerName }) => {

  if (loading) {
    return (
      <Card className="p-8 bg-white border-gray-200 shadow-md">
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          <p className="text-sm text-gray-600">Calculating costs...</p>
        </div>
      </Card>
    );
  }

  if (!cogs) {
    return (
      <Card className="p-8 bg-white border-gray-200 shadow-md">
        <p className="text-sm text-gray-600 text-center py-12">
          Upload a file and configure parameters to see cost breakdown
        </p>
      </Card>
    );
  }

  const costItems = [
    { 
      label: 'Filament', 
      value: cogs.filament_cost, 
      color: '#3b82f6'
    },
    { 
      label: 'Electricity', 
      value: cogs.electrical_cost, 
      color: '#eab308'
    },
    { 
      label: 'Depreciation', 
      value: cogs.depreciation_cost, 
      color: '#a855f7'
    },
    { 
      label: 'Floor Space', 
      value: cogs.floor_space_cost, 
      color: '#22c55e'
    },
    { 
      label: 'Labour', 
      value: cogs.labour_cost, 
      color: '#f97316'
    },
  ];

  if (cogs.optional_components_cost > 0) {
    costItems.push({
      label: 'Components',
      value: cogs.optional_components_cost,
      color: '#ec4899'
    });
  }

  return (
    <Card className="p-6 bg-white border-gray-200 shadow-lg">
      <div className="space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-purple-100">
            <DollarSign className="h-5 w-5 text-purple-600" />
          </div>
          <h3 className="font-semibold text-lg text-gray-800 flex items-center">
            Cost Breakdown
            <InfoTooltip content="Total cost of goods sold (COGS) for your 3D print. Includes all direct costs: materials, electricity, machine depreciation, floor space, labor, and optional components. Use this to set profitable pricing." />
          </h3>
        </div>

        {/* Totals at Top */}
        <div className="space-y-3 p-5 rounded-xl bg-gradient-to-br from-purple-50 to-purple-50 border border-purple-200 shadow-sm">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Cost Per Part</span>
            <span className="text-3xl font-mono font-bold text-purple-700">
              ${cogs.total_cogs_per_part.toFixed(2)}
            </span>
          </div>
          <Separator className="bg-gray-300" />
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">Per Plate ({quantityPerPlate} parts)</span>
            <span className="text-lg font-mono font-semibold text-gray-700">
              ${cogs.total_cogs_per_plate.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Pie Chart Visualization */}
        {costItems.length > 0 && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 sm:p-6 mt-4 overflow-x-hidden">
            <h4 className="text-sm font-semibold mb-4 text-gray-700 text-center">Cost Distribution</h4>
            <div className="w-full overflow-x-hidden">
              <PieChart 
                data={costItems.filter(item => item.value > 0).map(item => ({
                  name: item.label,
                  value: item.value,
                  color: item.color
                }))}
                width={500}
                height={500}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
